import { EnvironmentInjector, inject, Injector, runInInjectionContext } from "@angular/core";
import { CanActivateChildFn, CanActivateFn, CanDeactivateFn, CanMatchFn, GuardResult, MaybeAsync } from "@angular/router";
import { firstValueFrom, Observable } from "rxjs";

type UnknownGuardFn = (...guardArgs: unknown[]) => MaybeAsync<GuardResult>;

/**
 * Concat the given CanActivateFn into a single one that runs serially.
 *
 * @param guards The guards to concat.
 * @returns A single guard function that runs the given guards serially.
 *
 * If given two or more guards, the resulting guard function will return a promise that resolves into the first non-true result,
 * otherwise it will resolve into the last result if all are true.
 *
 * It will return the given guard function if only one guard is provided, or a guard that always returns true if no guards are provided.
 *
 * @example concatCanActivateFn(guard1, guard2, guard3); // If guard1 returns an Observable that emits true,
 * // and then guard2 return a RedirectCommand, guard3 will not be called,
 * // and the resulting promise will resolve into guard2's RedirectCommand.
 * // Instead, if all guards return true, the promise will resolve into guard3 result.
 */
export function concatCanActivateFn(...guards: CanActivateFn[]): CanActivateFn {
  return concatGuardArray(<UnknownGuardFn[]>guards);
}

/**
 * Concat the given CanActivateChildFn into a single one that runs serially.
 *
 * @param guards The guards to concat.
 * @returns A single guard function that runs the given guards serially.
 *
 * If given two or more guards, the resulting guard function will return a promise that resolves into the first non-true result,
 * otherwise it will resolve into the last result if all are true.
 *
 * It will return the given guard function if only one guard is provided, or a guard that always returns true if no guards are provided.
 *
 * @example concatCanActivateFn(guard1, guard2, guard3); // If guard1 returns an Observable that emits true,
 * // and then guard2 return a RedirectCommand, guard3 will not be called,
 * // and the resulting promise will resolve into guard2's RedirectCommand.
 * // Instead, if all guards return true, the promise will resolve into guard3 result.
 */
export function concatCanActivateChildFn(...guards: CanActivateChildFn[]): CanActivateChildFn {
  return concatGuardArray(<UnknownGuardFn[]>guards);
}

/**
 * Concat the given CanMatchFn into a single one that runs serially.
 *
 * @param guards The guards to concat.
 * @returns A single guard function that runs the given guards serially.
 *
 * If given two or more guards, the resulting guard function will return a promise that resolves into the first non-true result,
 * otherwise it will resolve into the last result if all are true.
 *
 * It will return the given guard function if only one guard is provided, or a guard that always returns true if no guards are provided.
 *
 * @example concatCanActivateFn(guard1, guard2, guard3); // If guard1 returns an Observable that emits true,
 * // and then guard2 return a RedirectCommand, guard3 will not be called,
 * // and the resulting promise will resolve into guard2's RedirectCommand.
 * // Instead, if all guards return true, the promise will resolve into guard3 result.
 */
export function concatCanMatchFn(...guards: CanMatchFn[]): CanMatchFn {
  return concatGuardArray(<UnknownGuardFn[]>guards);
}

/**
 * Concat the given CanDeactivateFn into a single one that runs serially.
 *
 * @param guards The guards to concat.
 * @returns A single guard function that runs the given guards serially.
 *
 * If given two or more guards, the resulting guard function will return a promise that resolves into the first non-true result,
 * otherwise it will resolve into the last result if all are true.
 *
 * It will return the given guard function if only one guard is provided, or a guard that always returns true if no guards are provided.
 *
 * @example concatCanActivateFn(guard1, guard2, guard3); // If guard1 returns an Observable that emits true,
 * // and then guard2 return a RedirectCommand, guard3 will not be called,
 * // and the resulting promise will resolve into guard2's RedirectCommand.
 * // Instead, if all guards return true, the promise will resolve into guard3 result.
 */
export function concatCanDeactivateFn<T>(...guards: CanDeactivateFn<T>[]): CanDeactivateFn<T> {
  return concatGuardArray(<UnknownGuardFn[]>guards);
}

/*
  Based on:
  https://github.com/angular/angular/blob/8546b17adec01de69bf314a959ef2d12f6638eb9/packages/router/test/integration.spec.ts#L5157-L5194
*/
function concatGuardArray<G extends UnknownGuardFn>(guardFnArray: readonly (G | null | undefined)[]): G {
  if (guardFnArray === undefined || guardFnArray === null || guardFnArray.length < 1) {
    return <G>(() => true);
  }

  if (guardFnArray.length < 2) {
    return guardFnArray[0] ?? <G>(() => true);
  }

  guardFnArray = Array.from(guardFnArray);

  return <G>async function resultGuardFn(...args): Promise<GuardResult> {
    const injector: Injector = inject(EnvironmentInjector);

    let endResult: GuardResult = true;
    for (const guardFn of guardFnArray) {
      if (guardFn && typeof guardFn === "function") {
        const guardFnResult: MaybeAsync<GuardResult> = runInInjectionContext(injector, () => guardFn(...args));
        if (guardFnResult instanceof Promise) {
          endResult = await guardFnResult;
        } else if (guardFnResult instanceof Observable) {
          endResult = await firstValueFrom(guardFnResult);
        } else {
          endResult = guardFnResult;
        }
      }
      // Continue only while strictly true, not "truthy".
      if (!endResult || typeof endResult !== "boolean") {
        break;
      }
    }

    return endResult;
  };
}
